import Language from '@@/enums/Language'
import { aschehougOidcClient } from '@/aschehougOidcClient'

const { VITE_AM_CORE, VITE_API_CORE, VITE_CONTENT_API_URL, VITE_CORE_EXTERNAL_API_URL, VITE_CMS_REST_API, VITE_RDF_REST_API_URL } = import.meta.env

export const useVgsFetch = (lang?: Language) => {
  const vgsFetch = (
    baseUrl: string,
    isAuthorizedRequest = true,
    isJsonResponse = true,
  ) => async (path: string, { method = 'GET', headers, ...options }: RequestInit = {}) => {
    const isContentOrCmsRestApi = baseUrl === VITE_CONTENT_API_URL || baseUrl === VITE_CMS_REST_API
    const isExternalCoreApi = baseUrl === VITE_CORE_EXTERNAL_API_URL

    const requestHeaders = new Headers({
      Accept: 'application/json',
      ...(isContentOrCmsRestApi && {
        'X-Siteaccess': lang || Language.NB,
        'Accept': import.meta.env.VITE_HEADER_ACCEPT,
      }),
      ...(isExternalCoreApi && {
        'Content-Type': 'application/json',
      }),
      ...(isAuthorizedRequest && { Authorization: `Bearer ${aschehougOidcClient.getAccessToken()}` }),
      ...headers,
    })

    if (!requestHeaders.get('Content-type')) {
      requestHeaders.append('Content-type', import.meta.env.VITE_HEADER_CONTENT_TYPE)
    }

    const response = await fetch(baseUrl + path, {
      method,
      headers: requestHeaders,
      ...options,
    })

    if (!isJsonResponse) {
      return response
    }

    const data = await response.json()

    if (response.status === 403) {
      sessionStorage.setItem('modus-redirect-route', window.location.pathname + window.location.search)
      window.location.href = import.meta.env.VITE_HOST
    }

    if (!response.ok) {
      let statusMessage = data.message || response.statusText

      if (!statusMessage && 'ErrorMessage' in data) {
        statusMessage = data.ErrorMessage.errorMessage
      }

      throw createError({
        statusMessage: statusMessage ?? '',
        statusCode: response.status,
      })
    }

    return data
  }

  const fetchAmCore = vgsFetch(VITE_AM_CORE)
  const fetchCore = vgsFetch(VITE_API_CORE, false)
  const fetchFile = vgsFetch(VITE_CONTENT_API_URL, true, false)
  const fetchContent = vgsFetch(VITE_CONTENT_API_URL)
  const fetchCoreExternal = vgsFetch(VITE_CORE_EXTERNAL_API_URL)
  const fetchCmsRest = vgsFetch(VITE_CMS_REST_API)
  const fetchCoreExternalNoJson = vgsFetch(VITE_CORE_EXTERNAL_API_URL, true, false)
  const fetchRDF = vgsFetch(VITE_RDF_REST_API_URL)

  return {
    fetchAmCore,
    fetchCore,
    fetchFile,
    fetchContent,
    fetchCoreExternal,
    fetchCmsRest,
    fetchCoreExternalNoJson,
    fetchRDF,
  }
}
